import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Mail, FileText, Trash2, Lock, Download } from 'lucide-react';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Onglet « Suivi & rapports » d'un chantier — fonctionnalités du plan Signature :
// abonnés au suivi par email + rapports PDF (manuel et automatique).
export default function SignatureTools({ project, onProjectChange }) {
  const [followers, setFollowers] = useState(null);
  const [locked, setLocked] = useState(false);
  const [report, setReport] = useState({
    frequency: project.reportFrequency || '',
    recipients: project.reportRecipients || '',
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/followers`);
      setFollowers(data);
      setLocked(false);
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') setLocked(true);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  if (locked) {
    return (
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          Suivi client & rapports
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--text-light)', fontWeight: 400 }}>
            <Lock size={12} /> Signature
          </span>
        </h3>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-light)', lineHeight: 1.6 }}>
          Avec le plan <strong>Signature</strong> : vos clients et syndics s'abonnent aux
          mises à jour du chantier par email (digest quotidien), et vous envoyez
          automatiquement des <strong>rapports PDF d'avancement</strong> aux couleurs de
          votre entreprise, chaque semaine ou chaque mois.
        </p>
      </div>
    );
  }

  const removeFollower = async (id) => {
    if (!window.confirm('Retirer cet abonné ?')) return;
    try {
      await api.delete(`/projects/${project.id}/followers/${id}`);
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/projects/${project.id}/report.pdf?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${project.slug || project.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors de la génération du rapport');
    }
  };

  const saveReportSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/report-settings`, {
        frequency: report.frequency || null,
        recipients: report.recipients,
      });
      toast.success('Réglages du rapport enregistrés');
      onProjectChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Mail size={16} /> Abonnés au suivi par email
        </h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 12 }}>
          Les visiteurs de la page publique peuvent s'abonner aux mises à jour
          (double confirmation par email, un digest par jour maximum).
        </p>
        {!followers ? (
          <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>Chargement...</p>
        ) : followers.length === 0 ? (
          <p style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>Aucun abonné pour le moment.</p>
        ) : (
          followers.map(f => (
            <div key={f.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.88rem',
            }}>
              <span>
                {f.email}
                {!f.confirmedAt && (
                  <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#92400e', background: '#fef3c7', borderRadius: 99, padding: '2px 8px' }}>
                    non confirmé
                  </span>
                )}
              </span>
              <button className="btn btn-danger btn-sm" onClick={() => removeFollower(f.id)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <FileText size={16} /> Rapports PDF d'avancement
        </h3>
        <button className="btn btn-sm" onClick={downloadPdf} style={{ marginBottom: 14 }}>
          <Download size={13} /> Générer le rapport des 30 derniers jours
        </button>

        <form onSubmit={saveReportSettings}>
          <div className="form-row">
            <div className="form-group">
              <label>Envoi automatique</label>
              <select value={report.frequency} onChange={e => setReport({ ...report, frequency: e.target.value })}>
                <option value="">Désactivé</option>
                <option value="WEEKLY">Chaque semaine</option>
                <option value="MONTHLY">Chaque mois</option>
              </select>
            </div>
            <div className="form-group">
              <label>Destinataires (séparés par des virgules)</label>
              <input value={report.recipients} placeholder="syndic@exemple.fr, client@exemple.fr"
                onChange={e => setReport({ ...report, recipients: e.target.value })} />
            </div>
          </div>
          <button className="btn btn-primary btn-sm" disabled={busy}>Enregistrer</button>
        </form>
      </div>
    </>
  );
}
